import {
  Button,
  Menu,
  MenuItemRadio,
  MenuList,
  MenuPopover,
  MenuTrigger,
  MessageBar,
  MessageBarBody,
  Spinner,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { ArrowClockwise16Regular, Checkmark12Regular, ChevronDown12Regular } from '@fluentui/react-icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Device, FlowList, NetworkMatrix, ThroughputResult } from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import { useMe } from '../auth/MeProvider';
import { NoAccess } from '../components/NoAccess';
import { PageHeader } from '../components/PageHeader';
import { useShell } from '../lib/shell';
import { liveDeviceIds } from '../lib/status';
import { errorText } from '../lib/useRelay';
import { vt } from '../theme';
import { Inspector } from './map/Inspector';
import { buildLanes, liveSessions, mapState, postureText, relayMbit, seenText, type MapState } from './map/model';
import { Topology } from './map/Topology';

const useStyles = makeStyles({
  filters: { display: 'flex', alignItems: 'center', columnGap: '10px', flexShrink: 0 },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: '8px',
    height: '22px',
    paddingInline: '10px 8px',
    borderRadius: tokens.borderRadiusCircular,
    border: `1px solid ${vt.surface2}`,
    backgroundColor: vt.surface3,
    color: vt.fg3,
    fontFamily: 'inherit',
    fontSize: '10.5px',
    cursor: 'pointer',
    ':hover': { color: vt.fg2 },
    ':focus-visible': { outline: `2px solid ${vt.brand}`, outlineOffset: '1px' },
  },
  chipOn: { color: vt.fg2 },
  chipMark: { display: 'flex', color: vt.fg4, width: '12px' },
  count: { marginLeft: 'auto', fontSize: '10.5px', color: vt.fg4 },
  scroll: { overflowX: 'auto', flexShrink: 0 },
  legend: { display: 'flex', alignItems: 'center', columnGap: '18px', listStyleType: 'none', margin: 0, padding: 0 },
  legendItem: { display: 'inline-flex', alignItems: 'center', columnGap: '7px', fontSize: '10.5px', color: vt.fg3 },
  legendDot: { width: '6.8px', height: '6.8px', borderRadius: '50%' },
  sessions: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '8px',
    margin: 0,
    padding: '14px 0 0',
    listStyleType: 'none',
    borderTop: `1px solid ${vt.surface2}`,
  },
  session: { display: 'flex', alignItems: 'center', columnGap: '12px', fontSize: '11px', whiteSpace: 'nowrap' },
  sessionDot: {
    width: '7.2px',
    height: '7.2px',
    borderRadius: '50%',
    backgroundColor: vt.cyan,
    boxShadow: `0 0 0 3px ${vt.tintCyan}`,
    flexShrink: 0,
  },
  sessionPath: { fontFamily: tokens.fontFamilyMonospace, color: vt.cyan },
  sessionMeta: { fontFamily: tokens.fontFamilyMonospace, color: vt.fg3, flexGrow: 1 },
  sessionNote: { fontSize: '10.5px', color: vt.fg4 },
  quiet: { fontSize: '11px', color: vt.fg4 },
});

const LEGEND: [string, string][] = [
  ['Live', vt.cyan],
  ['Online', vt.green],
  ['Idle', vt.fg3],
  ['Offline', vt.fg4],
];

/** The matrix, the live flows and the relay's last-hour rate. Loaded on open and on Refresh: nothing polls. */
function useMapData() {
  const { api } = useAuth();
  const [matrix, setMatrix] = useState<NetworkMatrix | null>(null);
  const [flows, setFlows] = useState<FlowList | null>(null);
  const [rate, setRate] = useState<ThroughputResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const seq = useRef(0);

  const reload = useCallback(async () => {
    const mine = ++seq.current;
    // Each is optional to the drawing, so one failing does not blank the others.
    const [m, f, t] = await Promise.allSettled([
      api.networkMatrix(),
      api.listFlows({ result: 'allowed' }),
      api.throughput('1h', 'total'),
    ]);
    if (mine !== seq.current) return;
    if (m.status === 'fulfilled') setMatrix(m.value);
    if (f.status === 'fulfilled') setFlows(f.value);
    if (t.status === 'fulfilled') setRate(t.value);
    const failed = [m, f, t].find((r) => r.status === 'rejected');
    setError(failed ? errorText(failed.reason) : null);
    setNow(Date.now());
  }, [api]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { matrix, flows, rate, error, now, reload };
}

function MapScreen() {
  const s = useStyles();
  const shell = useShell();
  const { relay, devices, statuses, networks, networksError, devicesError } = shell;
  const data = useMapData();
  const { now } = data;

  const [network, setNetwork] = useState<string>('all');
  const [showOffline, setShowOffline] = useState(true);
  const [showLive, setShowLive] = useState(true);
  // ?device=<id> opens the inspector on that device (a deep link, and what the screenshots use).
  const [params] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string | null>(() => params.get('device'));

  const lanes = useMemo(() => buildLanes(networks ?? [], devices ?? []), [networks, devices]);
  const live = useMemo(() => liveDeviceIds(data.flows?.flows), [data.flows]);
  const statusOf = useMemo(() => new Map((statuses ?? []).map((x) => [x.deviceId, x])), [statuses]);
  const states = useMemo(() => {
    const m = new Map<string, MapState>();
    for (const l of lanes) for (const d of l.devices) m.set(d.id, mapState(d.id, statusOf.get(d.id), live, now));
    return m;
  }, [lanes, statusOf, live, now]);
  const stateOf = useCallback((id: string) => states.get(id) ?? 'offline', [states]);
  const sessions = useMemo(() => liveSessions(data.flows?.flows, lanes), [data.flows, lanes]);

  const shown = useMemo(
    () =>
      lanes
        .filter((l) => network === 'all' || l.id === network)
        .map((l) => (showOffline ? l : { ...l, devices: l.devices.filter((d) => stateOf(d.id) !== 'offline') })),
    [lanes, network, showOffline, stateOf],
  );

  const total = devices?.length ?? 0;
  const visible = shown.reduce((n, l) => n + l.devices.length, 0);
  const selected: Device | undefined = devices?.find((d) => d.id === selectedId);
  const networkLabel = network === 'all' ? 'All networks' : (lanes.find((l) => l.id === network)?.name ?? network);

  const refresh = () => {
    void data.reload();
    void relay.refresh();
    void shell.reloadDevices();
    void shell.reloadStatuses();
    void shell.reloadNetworks();
  };

  const loading = !devices || (!networks && !networksError);
  const error = devicesError ?? networksError ?? data.error;

  return (
    <>
      <PageHeader
        title="Network map"
        actions={
          <Tooltip content="Refresh" relationship="label">
            <Button appearance="outline" shape="circular" size="small" icon={<ArrowClockwise16Regular />} onClick={refresh} />
          </Tooltip>
        }
      />

      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      <div className={s.filters} role="toolbar" aria-label="Map filters">
        <Menu checkedValues={{ network: [network] }} onCheckedValueChange={(_e, d) => setNetwork(d.checkedItems[0] ?? 'all')}>
          <MenuTrigger disableButtonEnhancement>
            <button type="button" className={mergeClasses(s.chip, s.chipOn)} aria-label={`Network: ${networkLabel}`}>
              {networkLabel}
              <span className={s.chipMark}>
                <ChevronDown12Regular />
              </span>
            </button>
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuItemRadio name="network" value="all">
                All networks
              </MenuItemRadio>
              {lanes.map((l) => (
                <MenuItemRadio key={l.id} name="network" value={l.id}>
                  {l.name}
                </MenuItemRadio>
              ))}
            </MenuList>
          </MenuPopover>
        </Menu>
        {(
          [
            ['Offline shown', showOffline, setShowOffline],
            ['Live traffic', showLive, setShowLive],
          ] as const
        ).map(([label, on, set]) => (
          <button
            key={label}
            type="button"
            className={mergeClasses(s.chip, on && s.chipOn)}
            aria-pressed={on}
            onClick={() => set(!on)}
          >
            {label}
            <span className={s.chipMark}>{on && <Checkmark12Regular />}</span>
          </button>
        ))}
        <span className={s.count}>
          {visible === total ? `${total} device${total === 1 ? '' : 's'}` : `${visible} of ${total} devices`}
        </span>
      </div>

      {loading ? (
        <Spinner label="Loading the map" />
      ) : (
        <div className={s.scroll}>
          <Topology
            relay={relay.relay}
            relayMbit={relayMbit(data.rate, now)}
            peers={total}
            policyVersion={data.matrix?.version ?? null}
            now={now}
            lanes={shown}
            stateOf={stateOf}
            seenOf={(id) => seenText(stateOf(id), statusOf.get(id), now)}
            postureOf={(id) => postureText(id, data.matrix)}
            sessions={showLive ? sessions : []}
            selectedId={selectedId}
            onSelect={(d) => setSelectedId(d.id === selectedId ? null : d.id)}
          />
        </div>
      )}

      <ul className={s.legend} aria-label="Legend">
        {LEGEND.map(([label, color]) => (
          <li key={label} className={s.legendItem}>
            <span className={s.legendDot} style={{ backgroundColor: color }} aria-hidden />
            {label}
          </li>
        ))}
      </ul>

      {showLive && (
        <ul className={s.sessions} aria-label="Live sessions">
          {sessions.length === 0 && <li className={s.quiet}>No live sessions between devices right now.</li>}
          {sessions.map((x) => (
            <li key={x.id} className={s.session} data-session={x.id}>
              <span className={s.sessionDot} aria-hidden />
              <span className={s.sessionPath}>{x.path}</span>
              <span className={s.sessionMeta}>{x.meta}</span>
              {x.sameNote && <span className={s.sessionNote}>{x.sameNote}</span>}
            </li>
          ))}
        </ul>
      )}

      {selected && (
        <Inspector
          device={selected}
          lane={lanes.find((l) => l.devices.some((d) => d.id === selected.id))}
          state={stateOf(selected.id)}
          status={statusOf.get(selected.id)}
          sessions={sessions.filter((x) => x.srcId === selected.id || x.dstId === selected.id)}
          now={now}
          onClose={() => setSelectedId(null)}
        />
      )}
    </>
  );
}

/** Mockup 02, admin only (it needs GET /networks): the relay, a lane per network, and the live paths through it. */
export function NetworkMap() {
  const { me, loading, error, isAdmin } = useMe();
  return (
    <>
      {loading && <Spinner label="Loading your permissions" />}
      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}
      {me && !isAdmin && (
        <>
          <PageHeader title="Network map" />
          <NoAccess />
        </>
      )}
      {me && isAdmin && <MapScreen />}
    </>
  );
}
